/*
 * ============================================================================
 *  SECURITY-CRITICAL -- see .github/CODEOWNERS for required review on this file
 * ============================================================================
 *
 *  The only sanctioned way to turn an attacker-controlled `?next=` query
 *  parameter into something we are willing to navigate to.
 *
 *  Background, in full: threat-models/authentication-and-sessions.md#T-15.
 *  The short version, because it changes how you should edit this file: the
 *  January 2023 fix (PR #2750) guarded this with `$router.resolve()`, which is
 *  a route matcher and not an origin check. It passed `//evil.example` through
 *  unchanged, so the open redirect stayed live for three years behind a guard
 *  that read like a guard.
 *
 *  THE RULE
 *  --------
 *  A `next` value is acceptable if and only if it is a site-root-relative path
 *  on our own origin. Not a full URL, not a protocol-relative reference, not a
 *  scheme, not a host. Anything else is discarded and replaced with a safe
 *  in-app default. We fail closed.
 *
 *  Do not reintroduce `$router.resolve()`, `startsWith('/')`, or any other
 *  single-check shortcut as the guard. Defense is layered on purpose: each
 *  layer alone would be a plausible fix, and the point is that a mistake in one
 *  is caught by the next. Removing a layer because "the one below it already
 *  covers that" is exactly the reasoning that produced the 2023 non-fix.
 *
 *  The last layer validates the value being RETURNED, not the value that came
 *  in. That is load-bearing: the first version of this module checked only the
 *  input, and `?next=/..//evil.example` walked straight through it, because the
 *  URL parser collapses the dot segment into a leading "//" that no earlier
 *  layer ever saw. Do not remove it.
 *
 *  TO NAVIGATE, USE `safeNextPath` OR `safeNextPathOrNull`.
 *  Both return a value rebuilt by the URL parser rather than echoed from the
 *  input, so the result cannot carry attacker-chosen structure beyond path,
 *  query, and fragment. `isSafeNextPath` is a predicate for tests and for
 *  guards that need to branch -- do not pair it with navigating to the raw
 *  input, which throws that layer away.
 *
 *  safeNextPath.test.js and nextPathWiring.test.js enforce all of this on every
 *  push. Do not delete cases from them to make a change pass.
 *
 * ============================================================================
 */

// Where users land when `next` is missing, malformed, or hostile.
export const DEFAULT_NEXT_PATH = '/account'

// Parsing base. Deliberately a constant on a reserved TLD (RFC 2606) rather
// than window.location.origin: the safety decision must not depend on which
// host the bundle happens to be running on, and must behave identically in
// jsdom, under SSR, and in the browser. Exported so tests assert against this
// constant rather than a second copy of the string.
export const SENTINEL_ORIGIN = 'https://safe-next-path.invalid'

// A bound to keep pathological input away from the regex engine and the URL
// parser. This is hygiene, NOT the security control -- the origin checks below
// are what stop an open redirect -- so it is set generously on purpose.
//
// Sized against the real worst case rather than a round number: the deepest
// icon-search state the app can produce (all 64 categories, every family,
// style, icon pack and visual tag, plus a query and a page) is ~1330 chars,
// which yields a ~1590-char sign-in URL. 4096 leaves roughly 3x headroom while
// still capping the resulting URL below the 8KB request-line limit nginx and
// most servers default to. Rejecting here is silent from the user's side --
// they simply lose their place and land on /account -- so do not tighten this
// without re-measuring.
export const MAX_LENGTH = 4096

// C0 controls, space, DEL, and C1 controls. Browsers strip tab/LF/CR from URLs
// *before* parsing, so a payload like "/<tab>/evil.example" has to be rejected
// here rather than handed to a parser that no longer sees the separator.
// no-control-regex is disabled deliberately: matching control characters is the
// entire purpose of this pattern. Do not "fix" the lint by narrowing the range.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARACTERS = /[\u0000-\u0020\u007F-\u009F]/

/**
 * Layers 1-2: is this even a candidate, and is it free of characters that
 * change how it parses downstream?
 *
 * Vue Router hands back a string, an array (repeated query param
 * `?next=a&next=b`), null, or undefined. Only a plain string of sane length is
 * worth looking at.
 */
function hasUsableShape(next) {
  if (typeof next !== 'string') return false
  if (next.length === 0 || next.length > MAX_LENGTH) return false
  if (FORBIDDEN_CHARACTERS.test(next)) return false

  // Backslash is normalized to `/` by every major browser, which turns
  // "/\evil.example" into "//evil.example".
  return !next.includes('\\')
}

/**
 * Layer 3: the literal shape of a root-relative path -- exactly one leading
 * slash. A second slash makes it protocol-relative (`//host`), which is
 * off-origin. This also rejects bare `foo`, `https://...`, `javascript:`,
 * `mailto:` and every other scheme, since none of them start with `/`.
 */
function isRootRelativeLiteral(next) {
  return next.charAt(0) === '/' && next.charAt(1) !== '/'
}

/**
 * The value this module hands back: path, query and fragment, with the origin
 * dropped. Everything that leaves here goes through this function, so it is
 * also the thing Layer 5 validates.
 */
function toRelativePath(url) {
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Layer 4: parse it the way a browser would and confirm the result really did
 * stay on the origin it was resolved against. The backstop that catches
 * anything the literal checks did not anticipate. Fails closed if the parser
 * refuses the input at all.
 *
 * Single point of parsing for the whole module, so a candidate is never parsed
 * twice and no caller can assume a parse "cannot throw here" because some other
 * function already tried it.
 *
 * @param {unknown} next raw, untrusted value
 * @returns {URL|null} the parsed URL when every layer passes, otherwise null
 */
function parseSameOrigin(next) {
  if (!hasUsableShape(next) || !isRootRelativeLiteral(next)) return null

  let parsed

  try {
    parsed = new URL(next, SENTINEL_ORIGIN)
  } catch {
    return null
  }

  if (parsed.origin !== SENTINEL_ORIGIN) return null

  // Layer 5 -- VALIDATE THE OUTPUT, NOT JUST THE INPUT.
  //
  // This is not belt-and-braces; it closes a live bypass. The parser resolves
  // dot segments, so the string we are about to hand back is NOT the string
  // checked by the layers above:
  //
  //     "/..//evil.example"  -> pathname "//evil.example"   -> off-origin
  //     "/.//evil.example"   -> pathname "//evil.example"   -> off-origin
  //     "/a/..//evil.example"-> pathname "//evil.example"   -> off-origin
  //
  // Each of those passes the leading-slash check on the raw input (it starts
  // "/." , not "//"), and the origin check passes too because resolving against
  // a base cannot leave the base's origin. Only the composed return value shows
  // the problem. Re-checking it here is what makes the guard hold.
  //
  // The general lesson, which the first four layers all missed: a sanitizer
  // must validate the value it RETURNS, not the value it was given.
  return isRootRelativeLiteral(toRelativePath(parsed)) ? parsed : null
}

/**
 * Is this `next` value safe to navigate to?
 *
 * A predicate, for tests and for guards that need to branch. Callers that are
 * about to navigate should use `safeNextPath` or `safeNextPathOrNull` instead,
 * so they navigate to the parser-normalized value rather than the raw input.
 *
 * @param {unknown} next raw, untrusted value (typically `$route.query.next`)
 * @returns {boolean} true only for same-origin, root-relative paths
 */
export function isSafeNextPath(next) {
  return parseSameOrigin(next) !== null
}

/**
 * Normalize an untrusted `next` value into something safe to navigate to,
 * falling back when it is unusable so the caller never has to decide.
 *
 * There is deliberately no `fallback` parameter. It used to take one, and it
 * returned that value unvalidated -- so `safeNextPath(null, '//evil.example')`
 * handed back an off-origin destination, and `safeNextPath(null,
 * 'javascript:alert(1)')` handed back a script URL, from the function whose
 * entire job is to not do that. No caller ever passed one, which is the only
 * reason it was never a live bug. A sanitizer with a caller-supplied escape
 * hatch is not a sanitizer; if a caller wants somewhere other than
 * DEFAULT_NEXT_PATH on failure, it should use `safeNextPathOrNull` and decide
 * for itself, in the open, where a null goes.
 *
 * @param {unknown} next raw, untrusted value (typically `$route.query.next`)
 * @returns {string} a safe root-relative path
 */
export function safeNextPath(next) {
  const parsed = parseSameOrigin(next)

  return parsed ? toRelativePath(parsed) : DEFAULT_NEXT_PATH
}

/**
 * As `safeNextPath`, but for callers whose correct behavior on an unusable
 * value is to not navigate at all rather than to navigate somewhere else.
 *
 * @param {unknown} next raw, untrusted value (typically `$route.query.next`)
 * @returns {string|null} a safe root-relative path, or null if there isn't one
 */
export function safeNextPathOrNull(next) {
  const parsed = parseSameOrigin(next)

  return parsed ? toRelativePath(parsed) : null
}
